/**
 * Analytics Effects
 * NgRx effects for analytics side effects and API integration
 */
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, switchMap, withLatestFrom } from 'rxjs/operators';
import { AnalyticsService } from '../services/analytics.service';
import { NotificationService } from '@core/services/notification.service';
import * as AnalyticsActions from './analytics.actions';
import { selectDateRange, selectGroupBy } from './analytics.selectors';

@Injectable()
export class AnalyticsEffects {
  private actions$ = inject(Actions);
  private analyticsService = inject(AnalyticsService);
  private notificationService = inject(NotificationService);
  private store = inject(Store);

  loadOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.loadOverview),
      switchMap(({ startDate, endDate }) =>
        this.analyticsService.getOverview(startDate, endDate).pipe(
          map((overview) => AnalyticsActions.loadOverviewSuccess({ overview })),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to load analytics overview';
            this.notificationService.showError(message);
            return of(AnalyticsActions.loadOverviewFailure({ error: message }));
          })
        )
      )
    )
  );

  loadDocumentAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.loadDocumentAnalytics),
      switchMap(({ request }) =>
        this.analyticsService.getDocumentAnalytics(request).pipe(
          map((data) => AnalyticsActions.loadDocumentAnalyticsSuccess({ data })),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to load document analytics';
            this.notificationService.showError(message);
            return of(AnalyticsActions.loadDocumentAnalyticsFailure({ error: message }));
          })
        )
      )
    )
  );

  loadApiUsageAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.loadApiUsageAnalytics),
      switchMap(({ request }) =>
        this.analyticsService.getApiUsageAnalytics(request).pipe(
          map((data) => AnalyticsActions.loadApiUsageAnalyticsSuccess({ data })),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to load API usage analytics';
            this.notificationService.showError(message);
            return of(AnalyticsActions.loadApiUsageAnalyticsFailure({ error: message }));
          })
        )
      )
    )
  );

  loadCostAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.loadCostAnalytics),
      switchMap(({ request }) =>
        this.analyticsService.getCostAnalytics(request).pipe(
          map((data) => AnalyticsActions.loadCostAnalyticsSuccess({ data })),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to load cost analytics';
            this.notificationService.showError(message);
            return of(AnalyticsActions.loadCostAnalyticsFailure({ error: message }));
          })
        )
      )
    )
  );

  loadTemplateAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.loadTemplateAnalytics),
      switchMap(({ startDate, endDate }) =>
        this.analyticsService.getTemplateAnalytics(startDate, endDate).pipe(
          map((data) => AnalyticsActions.loadTemplateAnalyticsSuccess({ data })),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to load template analytics';
            this.notificationService.showError(message);
            return of(AnalyticsActions.loadTemplateAnalyticsFailure({ error: message }));
          })
        )
      )
    )
  );

  loadProviderAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.loadProviderAnalytics),
      switchMap(({ startDate, endDate }) =>
        this.analyticsService.getProviderAnalytics(startDate, endDate).pipe(
          map((data) => AnalyticsActions.loadProviderAnalyticsSuccess({ data })),
          catchError((error) => {
            const message = error.error?.detail || 'Failed to load provider analytics';
            this.notificationService.showError(message);
            return of(AnalyticsActions.loadProviderAnalyticsFailure({ error: message }));
          })
        )
      )
    )
  );

  exportReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.exportReport),
      switchMap(({ request }) =>
        this.analyticsService.exportReport(request).pipe(
          map((blob) => {
            const timestamp = new Date().toISOString().split('T')[0];
            const extension = request.format === 'excel' ? 'xlsx' : request.format;
            const filename = `${request.report_type}_${timestamp}.${extension}`;
            this.analyticsService.downloadExportedFile(blob, filename);
            this.notificationService.showSuccess('Report exported successfully!');
            return AnalyticsActions.exportReportSuccess({ blob, filename });
          }),
          catchError(() => {
            const message = 'Unable to generate the report. Please try again.';
            this.notificationService.showError(message);
            return of(AnalyticsActions.exportReportFailure({ error: message }));
          })
        )
      )
    )
  );

  refreshAllAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.refreshAllAnalytics),
      withLatestFrom(
        this.store.select(selectDateRange),
        this.store.select(selectGroupBy)
      ),
      switchMap(([_, dateRange, groupBy]) => [
        AnalyticsActions.loadOverview({
          startDate: dateRange.start_date,
          endDate: dateRange.end_date
        }),
        AnalyticsActions.loadDocumentAnalytics({
          request: { ...dateRange, group_by: groupBy }
        }),
        AnalyticsActions.loadApiUsageAnalytics({
          request: { ...dateRange, group_by: groupBy }
        }),
        AnalyticsActions.loadCostAnalytics({
          request: { ...dateRange, group_by: groupBy }
        }),
        AnalyticsActions.loadTemplateAnalytics({
          startDate: dateRange.start_date,
          endDate: dateRange.end_date
        }),
        AnalyticsActions.loadProviderAnalytics({
          startDate: dateRange.start_date,
          endDate: dateRange.end_date
        })
      ])
    )
  );

  setDateRange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.setDateRange),
      map(() => AnalyticsActions.refreshAllAnalytics())
    )
  );

  setGroupBy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.setGroupBy),
      map(() => AnalyticsActions.refreshAllAnalytics())
    )
  );
}

